import AuthService, { AUTH_CHANGED_EVENT } from "../../../../services/AuthService";
import { DIFFICULTIES, QUICK_PLAY_GRID } from "../routes";
import {
    gridSizeAvailable,
    levelAvailable,
} from "../components/WirePlayControls";
import { FREE_TIER_DIFFICULTIES } from "./access";
import { loadPlayGridPreferences, savePlayGridPreferences } from "./storage";

async function authenticatedUserId() {
    if (!(await AuthService.isAuthenticated())) {
        return null;
    }
    const user = await AuthService.getUser();
    const userId = user?.userId;
    return userId != null && String(userId).trim() ? String(userId).trim() : null;
}

/** Load saved grid/level for the signed-in user (null for guests or when unset). */
export async function loadAuthenticatedPlayGridPreferences() {
    const userId = await authenticatedUserId();
    if (!userId) return null;
    return loadPlayGridPreferences(userId);
}

/** Persist grid/level for the signed-in user; no-op for guests. */
export async function saveAuthenticatedPlayGridPreferences({ gridSize, difficulty } = {}) {
    const userId = await authenticatedUserId();
    if (!userId) return;
    savePlayGridPreferences(userId, { gridSize, difficulty });
}

function defaultGridSize(templates) {
    if (gridSizeAvailable(templates, QUICK_PLAY_GRID)) return QUICK_PLAY_GRID;
    if (gridSizeAvailable(templates, 6)) return 6;
    return QUICK_PLAY_GRID;
}

function defaultDifficulty(templates, gridSize) {
    if (levelAvailable(templates, gridSize, "easy")) return "easy";
    if (levelAvailable(templates, gridSize, "medium")) return "medium";
    if (levelAvailable(templates, gridSize, "hard")) return "hard";
    return "easy";
}

/**
 * Pick grid + difficulty for library cards / home, honoring session overrides then saved prefs.
 * @param {{
 *   templates?: Array,
 *   savedPrefs?: { gridSize?: number, difficulty?: string } | null,
 *   freeTier?: boolean,
 *   initialGridSize?: number | null,
 *   initialDifficulty?: string | null,
 *   progressSimple?: boolean,
 * }} opts
 */
export function pickPlayGridDefaults({
    templates = [],
    savedPrefs = null,
    freeTier = false,
    initialGridSize = null,
    initialDifficulty = null,
    progressSimple = false,
} = {}) {
    const tpls = Array.isArray(templates) ? templates : [];

    if (progressSimple) {
        const gs =
            initialGridSize != null && Number.isFinite(Number(initialGridSize))
                ? Number(initialGridSize)
                : QUICK_PLAY_GRID;
        const diff =
            initialDifficulty && DIFFICULTIES.includes(initialDifficulty)
                ? initialDifficulty
                : "easy";
        return { gridSize: gs, difficulty: diff };
    }

    let gridSize;
    if (initialGridSize != null && Number.isFinite(Number(initialGridSize))) {
        gridSize = Number(initialGridSize);
    } else if (
        savedPrefs?.gridSize != null
        && Number.isFinite(Number(savedPrefs.gridSize))
        && !freeTier
    ) {
        gridSize = Number(savedPrefs.gridSize);
    } else {
        gridSize = defaultGridSize(tpls);
    }
    if (freeTier) {
        gridSize = QUICK_PLAY_GRID;
    }

    let difficulty;
    if (initialDifficulty && DIFFICULTIES.includes(initialDifficulty)) {
        difficulty = initialDifficulty;
    } else if (
        savedPrefs?.difficulty
        && DIFFICULTIES.includes(savedPrefs.difficulty)
        && !freeTier
    ) {
        difficulty = savedPrefs.difficulty;
    } else {
        difficulty = defaultDifficulty(tpls, gridSize);
    }
    if (freeTier && !FREE_TIER_DIFFICULTIES.includes(difficulty)) {
        difficulty = "easy";
    }

    if (!gridSizeAvailable(tpls, gridSize)) {
        gridSize = defaultGridSize(tpls);
    }
    if (!levelAvailable(tpls, gridSize, difficulty)) {
        difficulty = defaultDifficulty(tpls, gridSize);
    }
    if (freeTier) {
        gridSize = QUICK_PLAY_GRID;
        if (!FREE_TIER_DIFFICULTIES.includes(difficulty)) {
            difficulty = "easy";
        }
    }

    return { gridSize, difficulty };
}

/** React hook helper — subscribe to auth changes and reload saved prefs. */
export function subscribeAuthenticatedPlayGridPreferences(onChange) {
    const handler = () => {
        void loadAuthenticatedPlayGridPreferences().then(onChange);
    };
    window.addEventListener(AUTH_CHANGED_EVENT, handler);
    void loadAuthenticatedPlayGridPreferences().then(onChange);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, handler);
}
